import type { StorefrontTagListResponseType } from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import {
  Button,
  DataButton,
  H4,
  H5,
  getDndKitSortableIndexes,
} from "@sellar-io/components";
import { DragDropProvider } from "@dnd-kit/react";
import type { ComponentProps, ReactElement } from "react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../../../components";
import { Alert } from "../../../../../components/alert/Alert";
import { NavigationBarMenu } from "../../../../../components/navigation/NavigationBar";
import { LAYOUT_GROUPING_GROUP } from "../../../../../constants/routes";
import { useProductUngrouped } from "../../../../../hooks/api/v2/product/useUngroupedProducts";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useStorefrontTagReOrder } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagReOrder";
import { useSellarSupplier } from "../../../../../hooks/useSellarSupplier";
import { CreateGroupDialog } from "./CreateGroupDialog";
import { BulkProductAssignmentDialog } from "./BulkProductAssignmentDialog";
import { SortableGroupListItem } from "./SortableGroupListItem";

type DragDropProviderProps = ComponentProps<typeof DragDropProvider>;

const reorder = (
  list: StorefrontTagListResponseType,
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  if (!removed) return list;
  result.splice(endIndex, 0, removed);
  return result;
};

export const StorefrontLayoutOverview = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const supplier = useSellarSupplier();
  const [createDialogActive, setCreateDialogActive] = useState(false);
  const tagsQuery = useStorefrontTagList({
    query: [
      { type: "SupplierId", value: supplier.id, modifier: "EQUALS" },
      { type: "isProductActive", value: true, modifier: "EQUALS" },
    ],
  });
  const ungroupedProductsQuery = useProductUngrouped();
  const reorderMutation = useStorefrontTagReOrder();
  const tagsFromQuery = useMemo(
    () => tagsQuery.data?.pages.flat(),
    [tagsQuery.data?.pages],
  );
  const [tagList, setTagList] = useState<StorefrontTagListResponseType>(
    () => tagsFromQuery ?? [],
  );
  const [previousTags, setPreviousTags] = useState(tagsFromQuery);

  if (tagsFromQuery !== previousTags) {
    setPreviousTags(tagsFromQuery);
    setTagList(tagsFromQuery ?? []);
  }

  const onDragEnd: DragDropProviderProps["onDragEnd"] = (event) => {
    if (event.canceled) return;
    const active = event.operation.source;
    const activeId = Number(active?.id);
    const sortableIndexes = getDndKitSortableIndexes(active);

    if (!active || !Number.isInteger(activeId) || !sortableIndexes) return;

    const { oldIndex, newIndex } = sortableIndexes;
    if (oldIndex === newIndex || !tagList[oldIndex] || !tagList[newIndex]) {
      return;
    }

    const previousList = tagList;
    setTagList(reorder(tagList, oldIndex, newIndex));

    const hasMovedDown = oldIndex < newIndex;
    const beforeLexoRank =
      tagList[hasMovedDown ? newIndex : newIndex - 1]?.lexoRank ?? null;
    const isLastGroup = !tagList[newIndex + 1];
    const afterLexoRank = isLastGroup
      ? null
      : (tagList[hasMovedDown ? newIndex + 1 : newIndex]?.lexoRank ?? null);

    reorderMutation.mutate(
      { storefrontTagId: activeId, beforeLexoRank, afterLexoRank },
      { onError: () => setTagList(previousList) },
    );
  };

  const ungroupedCount = ungroupedProductsQuery.data?.length ?? 0;
  const isLoading = tagsQuery.isLoading || ungroupedProductsQuery.isLoading;
  const isError = tagsQuery.isError || ungroupedProductsQuery.isError;

  return (
    <>
      <NavigationBarMenu title="Layout & Grouping" borderBottom={false} />
      <main className="mx-auto flex w-full max-w-230 items-center gap-2">
        <div className="mt-2 mb-24 flex w-full min-w-0 flex-col gap-8 px-4 xl:mt-10">
          <header className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
            <H4 className="font-semibold">Layout & Grouping</H4>
            <p>Organise how your products are displayed on your storefront.</p>
          </header>

          <section className="flex flex-col gap-4">
            <div className="flex min-w-0 items-start justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1">
                <H5>Filter groups</H5>
                <span className="text-zinc-500">
                  Create as many groups as you need for your products, and drag
                  and drop to edit their order.
                </span>
              </div>
              <DataButton
                border={true}
                className="flex shrink-0 items-center gap-1"
                onClick={() => setCreateDialogActive(true)}
              >
                <Icon icon="PLUS" />
                New group
              </DataButton>
            </div>

            {isError && (
              <Alert
                type="error"
                title="Layout and grouping could not be loaded"
                message="Try loading your groups again."
              >
                <DataButton
                  border={true}
                  onClick={() => {
                    void tagsQuery.refetch();
                    void ungroupedProductsQuery.refetch();
                  }}
                >
                  Try again
                </DataButton>
              </Alert>
            )}

            {reorderMutation.isError && (
              <Alert
                type="error"
                title="The group order was not saved"
                message="The previous order has been restored. Try moving the group again."
                onDismiss={() => reorderMutation.reset()}
              />
            )}

            {isLoading ? (
              <SpinnerLegacy />
            ) : (
              !isError && (
                <>
                  <Alert
                    type="information"
                    className="w-full"
                    title={
                      ungroupedCount > 0
                        ? `You have ${ungroupedCount} ungrouped ${pluralize("product", ungroupedCount)}`
                        : "All active products are grouped"
                    }
                    message={
                      ungroupedCount > 0
                        ? "Help customers find your products on your storefront by sorting them under a group."
                        : "Create or edit groups to organise your storefront."
                    }
                  >
                    {ungroupedCount > 0 && (
                      <Button
                        color="transparent"
                        className="border border-solid border-blue-300 text-blue-800"
                        onClick={() => {
                          const nextParams = new URLSearchParams(searchParams);
                          nextParams.set("ungrouped", "true");
                          setSearchParams(nextParams);
                        }}
                      >
                        Group products
                      </Button>
                    )}
                  </Alert>

                  {tagList.length === 0 ? (
                    <Alert
                      type="information"
                      title="Create your first filter group"
                      message="Groups make it easier for customers to browse your products."
                    >
                      <DataButton
                        border={true}
                        onClick={() => setCreateDialogActive(true)}
                      >
                        Create group
                      </DataButton>
                    </Alert>
                  ) : (
                    <DragDropProvider onDragEnd={onDragEnd}>
                      <div
                        className="flex flex-col gap-2"
                        aria-busy={reorderMutation.isPending}
                      >
                        {tagList.map((tag, index) => (
                          <SortableGroupListItem
                            key={tag.id}
                            tag={tag}
                            index={index}
                            disabled={reorderMutation.isPending}
                            onClick={() =>
                              navigate(
                                LAYOUT_GROUPING_GROUP.href({ groupId: tag.id }),
                              )
                            }
                          />
                        ))}
                      </div>
                    </DragDropProvider>
                  )}
                </>
              )
            )}
          </section>
        </div>
      </main>

      {createDialogActive && (
        <CreateGroupDialog onDismiss={() => setCreateDialogActive(false)} />
      )}
      {searchParams.get("ungrouped") === "true" && (
        <BulkProductAssignmentDialog
          active={searchParams.get("ungrouped") === "true"}
          onDismiss={() => {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("ungrouped");
            setSearchParams(nextParams, { replace: true });
          }}
          products={ungroupedProductsQuery.data ?? []}
          title="Group ungrouped products"
        />
      )}
      <span className="sr-only" aria-live="polite">
        {reorderMutation.isPending ? "Saving group order" : ""}
      </span>
    </>
  );
};
